import { useState } from "react";
import { UserDto, userFormData, userIdKey } from "../types/user";
import { userService } from "../services/userService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useUsers = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: users = [],
    loading: isFetching,
    error: getError,
    refetch: fetchUsers,
    invalidateCache,
  } = useApi<UserDto[]>("users", (signal) => userService.getAll({}, signal));

  // 2. CREATE
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newUser: typeof userFormData) => userService.create(newUser));

  // 3. UPDATE
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number; data: typeof userFormData }) => 
    userService.update(id, data)
  );

  // 4. DELETE
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => userService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createUser = async (newUser: typeof userFormData): Promise<void> => {
    try {
      await createExecution(newUser);
      
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο χρήστης αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchUsers(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  // Ορισμός της συνάρτησης updateUser
  const updateUser = async (id: number, updatedData: typeof userFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο χρήστης ενημερώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchUsers(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά την ενημέρωση";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteUsers = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchUsers(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    users,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchUsers: () => fetchUsers(true),
    createUser,
    updateUser,
    deleteUsers,
    modalState,
    closeModal,
  };
};